import { formatLocal } from "@pipeline/core/time.ts";
import { TIMEZONE } from "./data.ts";

/** "4 min ago", "2 h ago". Falls back to an absolute time past a day. */
export function relativeTime(iso: string, now = Date.now()): string {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return "";

  const seconds = Math.round((now - then) / 1000);
  if (seconds < 45) return "just now";
  if (seconds < 90) return "1 min ago";

  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;

  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;

  return formatLocal(new Date(then), TIMEZONE);
}

/**
 * Compact stamp for a list that reaches back past today.
 *
 * `relativeTime` falls back to a full `Aug 12, 2026, 5:04 AM GMT+2` once a row
 * is more than a day old, which is right for Today (where nothing ever is) and
 * wrong for a historical list (where almost everything is): it wraps to three
 * lines in a table column. Inside a day this stays relative, because "14 min
 * ago" is what you want to know about something still moving. Past that it
 * becomes a date, and the exact stamp lives in the lead's detail panel.
 *
 * Explicit components, never `dateStyle`: `Intl.DateTimeFormat` throws when
 * `dateStyle` is combined with `timeZoneName`, and that once broke every lead
 * email in this codebase.
 */
export function listTime(iso: string, now = Date.now()): string {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return "";
  if (now - then < 24 * 3_600_000) return relativeTime(iso, now);

  const year = new Intl.DateTimeFormat("en-GB", { timeZone: TIMEZONE, year: "numeric" });
  const sameYear = year.format(new Date(then)) === year.format(new Date(now));

  return new Intl.DateTimeFormat("en-GB", {
    timeZone: TIMEZONE,
    day: "numeric",
    month: "short",
    ...(sameYear ? {} : { year: "2-digit" }),
  }).format(new Date(then));
}

export function absoluteTime(iso: string): string {
  const parsed = Date.parse(iso);
  if (Number.isNaN(parsed)) return "";
  return formatLocal(new Date(parsed), TIMEZONE);
}

/** Long local date for the Today heading. */
export function longDate(dateKey: string): string {
  const [y, m, d] = dateKey.split("-").map(Number);
  if (!y || !m || !d) return dateKey;
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: TIMEZONE,
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(new Date(Date.UTC(y, m - 1, d, 12)));
}

export function duration(ms: number): string {
  const minutes = Math.round(ms / 60_000);
  if (minutes < 1) return "under a minute";
  if (minutes < 60) return `${minutes} min`;
  const hours = ms / 3_600_000;
  if (hours < 48) return `${hours.toFixed(1)} h`;
  return `${Math.round(hours / 24)} days`;
}

export function plural(count: number, one: string, many = `${one}s`): string {
  return count === 1 ? one : many;
}
